/**
 * Module-level ref that non-React modules (notably the ky client in
 * `src/lib/client.ts`) use to trigger a full auth recovery: clear the poisoned
 * Auth0 cache and bounce the user back through Auth0.
 *
 * Mirrors the getAccessTokenSilentlyRef pattern — Auth0CookieSync (which runs
 * inside <Auth0Provider>) publishes the callback on mount.
 *
 * Why this exists: when Auth0 rejects the rotating refresh token, the SDK
 * throws but leaves the cached ID token in place, so auth0-react keeps
 * reporting isAuthenticated === true forever. Every request 401s, no route
 * guard fires, and the app renders a blank page. Recovery has to be driven
 * explicitly.
 */
export const authRecoveryRef: { current: (() => Promise<void>) | null } = {
  current: null,
};

/**
 * Where to send the user once recovery completes.
 *
 * Recovery clears the Auth0 cache, which flips isAuthenticated to false and
 * bounces the router to /auth/signin — whose own effect fires a competing
 * loginWithRedirect({ returnTo: "/" }). Whichever redirect assigns
 * window.location last wins, so the target can't live only in the appState of
 * one of them. Parking it here makes both paths agree, and means the page the
 * user was actually on survives a session death instead of dumping them on the
 * app root. Consumed by onRedirectCallback.
 */
export const AUTH_RETURN_TO_KEY = "ts_auth_return_to";

/** Auth routes are transit, never a destination. */
const isTransit = (path: string) => path.startsWith("/auth/");

/**
 * The URL this page load started at, captured at module init.
 *
 * Recovery can't rely on reading window.location when it fires: a dead session
 * has no accessToken cookie, so RequireAuth navigates to /auth/signin on the
 * very first render — before the failed token mint has even rejected. By then
 * the deep link the user actually asked for is gone from the URL bar.
 */
const ENTRY_PATH = window.location.pathname + window.location.search;

/** Records where the user should end up after recovery completes. */
export function rememberReturnTo(): void {
  const current = window.location.pathname + window.location.search;
  const target = !isTransit(current)
    ? current
    : !isTransit(ENTRY_PATH)
      ? ENTRY_PATH
      : null;
  if (!target) return;
  sessionStorage.setItem(AUTH_RETURN_TO_KEY, target);
}

/**
 * Reads the remembered destination without clearing it, defaulting to the app
 * root. Deliberately non-destructive: both competing redirects read this, and
 * we can't know which one will actually win the navigation.
 */
export function peekReturnTo(): string {
  return sessionStorage.getItem(AUTH_RETURN_TO_KEY) || "/";
}

/** Clears the remembered destination once the round-trip has landed. */
export function clearReturnTo(): void {
  sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
}

/**
 * Auth0 error codes that mean "this session cannot be salvaged in the
 * background" — the only cure is sending the user back through /authorize.
 *
 * - invalid_grant          — the refresh token is gone from Auth0's store
 *                            (rotated away, revoked by reuse detection, expired)
 * - missing_refresh_token  — the SDK has no refresh token cached at all
 * - login_required         — the prompt=none iframe fallback found no SSO session
 * - consent_required       — same, but consent is outstanding
 */
const UNRECOVERABLE_AUTH_ERRORS = new Set([
  "invalid_grant",
  "missing_refresh_token",
  "login_required",
  "consent_required",
]);

export function isUnrecoverableAuthError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const { error, message } = err as { error?: unknown; message?: unknown };
  if (typeof error === "string" && UNRECOVERABLE_AUTH_ERRORS.has(error)) {
    return true;
  }
  // auth0-spa-js does not always populate `.error` (e.g. the "Missing Refresh
  // Token" GenericError raised before any network call), so also match the
  // message text the SDK itself branches on.
  if (typeof message === "string") {
    const lower = message.toLowerCase();
    return (
      lower.includes("missing refresh token") ||
      lower.includes("invalid refresh token")
    );
  }
  return false;
}
